import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MapPin, Clock, Calendar, ChevronLeft, ChevronRight, Check, Upload, X } from 'lucide-react';
import { useApp } from '../context/AppContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const DEPOSIT_AMOUNT = 30;
const ETRANSFER_EMAIL = 'gunandrive222@gmail.com';

const StudioBookingPage = () => {
  const { user } = useApp();
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState('industry');
  const [selectedDuration, setSelectedDuration] = useState(1);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [bookedSlots, setBookedSlots] = useState({ industry: [], rainforest: [] });
  const [loading, setLoading] = useState(false);
  const [bookingStep, setBookingStep] = useState('form'); // 'form', 'deposit', 'success'
  const [bookingId, setBookingId] = useState(null);
  const [depositProof, setDepositProof] = useState(null);
  const [depositPreview, setDepositPreview] = useState(null);
  const [submittingProof, setSubmittingProof] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    notes: ''
  });

  const rooms = [
    { id: 'industry', name: 'Industry Room', price: 70, description: 'Premium recording space with top-tier equipment' },
    { id: 'rainforest', name: 'Rainforest Room', price: 60, description: 'Creative space with a unique vibe' }
  ];

  const timeSlots = [
    '10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM', 
    '3:00 PM', '4:00 PM', '5:00 PM', '6:00 PM', '7:00 PM', '8:00 PM'
  ];

  const durations = [
    { hours: 1, label: '1 Hour' },
    { hours: 2, label: '2 Hours' },
    { hours: 3, label: '3 Hours' },
    { hours: 4, label: '4 Hours' },
    { hours: 5, label: '5 Hours' },
    { hours: 6, label: '6 Hours' }
  ];

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || ''
      }));
    }
  }, [user]);

  useEffect(() => {
    if (selectedDate) {
      fetchBookedSlots();
    }
  }, [selectedDate]);

  const fetchBookedSlots = async () => {
    try {
      const dateStr = selectedDate.toISOString().split('T')[0];
      const [industryRes, rainforestRes] = await Promise.all([
        axios.get(`${API}/studio/bookings/slots?date=${dateStr}&room=industry`),
        axios.get(`${API}/studio/bookings/slots?date=${dateStr}&room=rainforest`)
      ]);
      setBookedSlots({
        industry: industryRes.data.booked_slots || [],
        rainforest: rainforestRes.data.booked_slots || []
      });
    } catch (e) {
      console.error(e);
    }
  };

  const getSelectedRoomPrice = () => {
    const room = rooms.find(r => r.id === selectedRoom);
    return room ? room.price * selectedDuration : 0;
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    // Add empty slots for days before first day of month
    for (let i = 0; i < firstDay.getDay(); i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const isDateDisabled = (date) => {
    if (!date) return true;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Disable past dates
    if (date < today) return true;
    // Only allow Thursday (4), Friday (5), Saturday (6), Sunday (0)
    const dayOfWeek = date.getDay();
    const availableDays = [0, 4, 5, 6]; // Sun, Thu, Fri, Sat
    return !availableDays.includes(dayOfWeek);
  };

  const isTimeBooked = (time) => {
    return bookedSlots[selectedRoom]?.includes(time);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDate || !selectedTime) {
      toast.error('Please select a date and time');
      return;
    }

    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const totalPrice = getSelectedRoomPrice();
      const room = rooms.find(r => r.id === selectedRoom);
      const response = await axios.post(`${API}/studio/bookings`, {
        date: selectedDate.toISOString().split('T')[0],
        time: selectedTime,
        duration_hours: selectedDuration,
        room: selectedRoom,
        room_name: room.name,
        price: totalPrice,
        deposit_amount: DEPOSIT_AMOUNT,
        client_name: formData.name,
        client_email: formData.email,
        client_phone: formData.phone,
        notes: formData.notes
      });
      
      setBookingId(response.data.booking_id);
      setBookingStep('deposit');
      toast.success('Now send your $30 deposit to lock in your session!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit booking');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      setDepositProof(file);
      setDepositPreview(URL.createObjectURL(file));
    }
  };

  const handleDepositSubmit = async () => {
    if (!depositProof) {
      toast.error('Please upload a screenshot of your deposit');
      return;
    }

    setSubmittingProof(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Image = reader.result;
        
        await axios.post(`${API}/studio/bookings/${bookingId}/deposit`, {
          proof_image_url: base64Image
        });
        
        setBookingStep('success');
        toast.success('Deposit proof submitted! Your session is locked in.');
      };
      reader.readAsDataURL(depositProof);
    } catch (e) {
      toast.error('Failed to submit deposit proof. Please try again.');
    } finally {
      setSubmittingProof(false);
    }
  };

  const clearDepositImage = () => {
    setDepositProof(null);
    setDepositPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  // Deposit Step - Show e-transfer instructions and upload form
  if (bookingStep === 'deposit') {
    return (
      <div className="min-h-screen pt-24 pb-32 px-6 lg:px-12" data-testid="booking-deposit">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <div className="w-20 h-20 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mx-auto mb-6">
              <span className="text-3xl font-bold text-[#D4AF37]">$30</span>
            </div>
            <h1 className="text-3xl font-bold mb-4 text-white" style={{ fontFamily: 'Syne' }}>
              Send Your Deposit
            </h1>
            <p className="text-neutral-400">
              Send a <span className="text-[#D4AF37] font-bold">$30 CAD</span> deposit via e-transfer to lock in your session
            </p>
          </div>

          <div className="glass-card p-6 mb-6">
            <h3 className="font-bold mb-4 text-white" style={{ fontFamily: 'Syne' }}>E-Transfer Details</h3>
            <div className="space-y-4">
              <div className="flex justify-between py-3 border-b border-[#D4AF37]/10">
                <span className="text-neutral-400">Send to:</span>
                <span className="text-[#D4AF37] font-bold">{ETRANSFER_EMAIL}</span>
              </div>
              <div className="flex justify-between py-3 border-b border-[#D4AF37]/10">
                <span className="text-neutral-400">Amount:</span>
                <span className="text-white font-bold">${DEPOSIT_AMOUNT} CAD</span>
              </div>
              <div className="flex justify-between py-3 border-b border-[#D4AF37]/10">
                <span className="text-neutral-400">Reference:</span>
                <span className="text-white font-mono text-sm">{bookingId}</span>
              </div>
            </div>
            <div className="bg-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-xl p-4 mt-4">
              <p className="text-neutral-300 text-sm">
                Include your booking ID <span className="text-[#D4AF37] font-mono">{bookingId}</span> in the e-transfer message. 
                The remaining balance of <span className="text-[#D4AF37] font-bold">${getSelectedRoomPrice() - DEPOSIT_AMOUNT} CAD</span> is due at the studio.
              </p>
            </div>
          </div>

          <div className="glass-card p-6 mb-6">
            <h3 className="font-bold mb-4 text-white" style={{ fontFamily: 'Syne' }}>Session Details</h3>
            <div className="space-y-2 text-sm">
              <p className="text-neutral-400">Room: <span className="text-[#D4AF37] font-bold">{rooms.find(r => r.id === selectedRoom)?.name}</span></p>
              <p className="text-neutral-400">Date: <span className="text-white">{selectedDate?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</span></p>
              <p className="text-neutral-400">Time: <span className="text-white">{selectedTime}</span></p>
              <p className="text-neutral-400">Duration: <span className="text-white">{durations.find(d => d.hours === selectedDuration)?.label}</span></p>
              <p className="text-neutral-400">Total: <span className="text-[#D4AF37] font-bold">${getSelectedRoomPrice()} CAD</span></p>
              <p className="text-neutral-400">Deposit: <span className="text-white">-${DEPOSIT_AMOUNT} CAD</span></p>
              <p className="text-neutral-400 pt-2 border-t border-[#D4AF37]/10">Due at studio: <span className="text-white font-bold">${getSelectedRoomPrice() - DEPOSIT_AMOUNT} CAD</span></p>
            </div>
          </div>

          <div className="glass-card p-6">
            <h3 className="font-bold mb-4 text-white" style={{ fontFamily: 'Syne' }}>Upload Deposit Proof</h3>
            <p className="text-neutral-400 text-sm mb-4">
              After sending the e-transfer, upload a screenshot of your payment confirmation.
            </p>

            <div
              className="file-upload-area mb-4"
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
                data-testid="deposit-upload-input"
              />
              
              {depositPreview ? (
                <div className="relative">
                  <img 
                    src={depositPreview} 
                    alt="Deposit proof" 
                    className="max-h-48 mx-auto rounded-lg"
                  />
                  <button
                    onClick={(e) => { e.stopPropagation(); clearDepositImage(); }}
                    className="absolute top-2 right-2 p-2 bg-red-500/80 rounded-full hover:bg-red-500 transition-colors"
                  >
                    <X size={16} className="text-white" />
                  </button>
                </div>
              ) : (
                <>
                  <Upload size={48} className="mx-auto mb-4 text-[#D4AF37]/50" />
                  <p className="text-neutral-400 mb-2">Click to upload screenshot</p>
                  <p className="text-neutral-500 text-sm">PNG, JPG up to 5MB</p>
                </>
              )}
            </div>

            <button
              onClick={handleDepositSubmit}
              disabled={!depositProof || submittingProof}
              className={`btn-primary w-full flex items-center justify-center gap-2 ${!depositProof ? 'opacity-50 cursor-not-allowed' : ''}`}
              data-testid="submit-deposit-btn"
            >
              {submittingProof ? (
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
              ) : (
                'Submit Deposit Proof'
              )}
            </button>
          </div>

          <p className="text-center text-yellow-500 text-sm mt-6">
            ⚠️ Your session is not confirmed until deposit is received and verified
          </p>
        </div>
      </div>
    );
  }

  // Success Step
  if (bookingStep === 'success') {
    return (
      <div className="min-h-screen pt-24 pb-32 px-6 lg:px-12" data-testid="booking-success">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <Check size={40} className="text-green-500" />
          </div>
          <h1 className="text-3xl font-bold mb-4 text-white" style={{ fontFamily: 'Syne' }}>
            Session Locked In!
          </h1>
          <p className="text-neutral-400 mb-6">
            Your deposit has been submitted. You'll receive confirmation at <span className="text-[#D4AF37]">{formData.email}</span> once verified.
          </p>
          <div className="glass-card p-6 text-left mb-8">
            <h3 className="font-bold mb-4 text-white">Booking Details</h3>
            <div className="space-y-2 text-sm">
              <p className="text-neutral-400">Room: <span className="text-[#D4AF37] font-bold">{rooms.find(r => r.id === selectedRoom)?.name}</span></p>
              <p className="text-neutral-400">Date: <span className="text-white">{selectedDate?.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span></p>
              <p className="text-neutral-400">Time: <span className="text-white">{selectedTime}</span></p>
              <p className="text-neutral-400">Duration: <span className="text-white">{durations.find(d => d.hours === selectedDuration)?.label}</span></p>
              <p className="text-neutral-400">Total: <span className="text-[#D4AF37] font-bold">${getSelectedRoomPrice()} CAD</span></p>
              <p className="text-green-500">✓ Deposit paid: ${DEPOSIT_AMOUNT} CAD</p>
              <p className="text-neutral-400">Due at studio: <span className="text-white font-bold">${getSelectedRoomPrice() - DEPOSIT_AMOUNT} CAD</span></p>
            </div>
          </div>
          <div className="glass-card p-6 text-left">
            <div className="flex items-start gap-3">
              <MapPin size={20} className="text-[#D4AF37] mt-1 flex-shrink-0" />
              <div>
                <p className="font-bold text-white">No Losses Studio</p>
                <p className="text-neutral-400">151 Brock Street North</p>
                <p className="text-neutral-400">Whitby, Ontario</p>
              </div>
            </div>
          </div>
          <button 
            onClick={() => { setBookingStep('form'); setSelectedDate(null); setSelectedTime(null); setDepositProof(null); setDepositPreview(null); }}
            className="btn-secondary mt-8"
          >
            Book Another Session
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-32 px-6 lg:px-12" data-testid="studio-booking-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ fontFamily: 'Syne' }}>
            <span className="brand-font text-[#D4AF37]">Studio</span>
            <span className="text-white"> Booking</span>
          </h1>
          <p className="text-neutral-500 text-lg max-w-2xl mx-auto">
            Book your session at No Losses Studio. Professional recording environment, premium equipment, and expert engineering available.
          </p>
        </div>

        {/* Studio Info Card */}
        <div className="glass-card p-6 mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#B8941F] flex items-center justify-center flex-shrink-0">
                <MapPin size={24} className="text-black" />
              </div>
              <div>
                <h2 className="font-bold text-xl text-white" style={{ fontFamily: 'Syne' }}>No Losses Studio</h2>
                <p className="text-neutral-400">151 Brock Street North, Whitby</p>
                <a 
                  href="https://maps.google.com/?q=151+Brock+Street+North+Whitby+Ontario" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#D4AF37] text-sm hover:text-[#FFD700] transition-colors"
                >
                  Get Directions →
                </a>
              </div>
            </div>
            <div className="flex flex-col gap-1 text-neutral-400 text-sm">
              <div className="flex items-center gap-2">
                <Clock size={16} className="text-[#D4AF37]" />
                <span>10:00 AM - 10:00 PM</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar size={16} className="text-[#D4AF37]" />
                <span className="text-[#D4AF37] font-medium">Open Thu - Sun</span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Calendar */}
          <div className="lg:col-span-2">
            <div className="glass-card p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-bold text-xl text-white" style={{ fontFamily: 'Syne' }}>
                  <Calendar size={20} className="inline mr-2 text-[#D4AF37]" />
                  Select Date
                </h3>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                    className="p-2 text-neutral-400 hover:text-[#D4AF37] transition-colors"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <span className="text-white font-medium min-w-[140px] text-center">
                    {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                  </span>
                  <button 
                    onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                    className="p-2 text-neutral-400 hover:text-[#D4AF37] transition-colors"
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-2 mb-6">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="text-center text-xs text-neutral-500 py-2">{day}</div>
                ))}
                {getDaysInMonth(currentMonth).map((date, i) => (
                  <button
                    key={i}
                    disabled={isDateDisabled(date)}
                    onClick={() => date && setSelectedDate(date)}
                    className={`aspect-square rounded-lg flex items-center justify-center text-sm transition-all ${
                      !date ? '' :
                      isDateDisabled(date) ? 'text-neutral-700 cursor-not-allowed' :
                      selectedDate?.toDateString() === date.toDateString() 
                        ? 'bg-[#D4AF37] text-black font-bold' 
                        : 'text-neutral-300 hover:bg-[#D4AF37]/20 hover:text-[#D4AF37]'
                    }`}
                    data-testid={date ? `date-${date.getDate()}` : undefined}
                  >
                    {date?.getDate()}
                  </button>
                ))}
              </div>

              {/* Time Slots */}
              {selectedDate && (
                <div>
                  <h4 className="font-bold text-white mb-4">
                    <Clock size={18} className="inline mr-2 text-[#D4AF37]" />
                    Available Times for {selectedDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </h4>
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                    {timeSlots.map(time => (
                      <button
                        key={time}
                        disabled={isTimeBooked(time)}
                        onClick={() => setSelectedTime(time)}
                        className={`py-3 px-2 rounded-lg text-sm font-medium transition-all ${
                          isTimeBooked(time) 
                            ? 'bg-neutral-800 text-neutral-600 cursor-not-allowed line-through' 
                            : selectedTime === time 
                              ? 'bg-[#D4AF37] text-black' 
                              : 'bg-neutral-800/50 text-neutral-300 hover:bg-[#D4AF37]/20 hover:text-[#D4AF37]'
                        }`}
                        data-testid={`time-${time.replace(/\s/g, '-')}`}
                      >
                        {time}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Booking Form */}
          <div className="lg:col-span-1">
            <div className="glass-card p-6 sticky top-28">
              <h3 className="font-bold text-xl text-white mb-6" style={{ fontFamily: 'Syne' }}>Book Session</h3>
              
              {/* Room Selection */}
              <div className="mb-6">
                <label className="block text-sm text-neutral-400 mb-2">Select Room</label>
                <div className="space-y-2">
                  {rooms.map(room => (
                    <button
                      key={room.id}
                      onClick={() => { setSelectedRoom(room.id); setSelectedTime(null); }}
                      className={`w-full p-4 rounded-xl text-left transition-all ${
                        selectedRoom === room.id 
                          ? 'bg-[#D4AF37]/20 border-2 border-[#D4AF37]' 
                          : 'bg-neutral-800/50 border-2 border-transparent hover:border-[#D4AF37]/30'
                      }`}
                      data-testid={`room-${room.id}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className={`font-bold ${selectedRoom === room.id ? 'text-[#D4AF37]' : 'text-white'}`}>{room.name}</span>
                        <span className={`font-bold ${selectedRoom === room.id ? 'text-[#D4AF37]' : 'text-neutral-400'}`}>${room.price}/hr</span>
                      </div>
                      <p className="text-xs text-neutral-500">{room.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Duration Selection */}
              <div className="mb-6">
                <label className="block text-sm text-neutral-400 mb-2">Session Duration</label>
                <div className="grid grid-cols-3 gap-2">
                  {durations.map(d => (
                    <button
                      key={d.hours}
                      onClick={() => setSelectedDuration(d.hours)}
                      className={`p-3 rounded-xl text-center transition-all ${
                        selectedDuration === d.hours 
                          ? 'bg-[#D4AF37]/20 border border-[#D4AF37] text-[#D4AF37]' 
                          : 'bg-neutral-800/50 border border-transparent text-neutral-400 hover:border-[#D4AF37]/30'
                      }`}
                      data-testid={`duration-${d.hours}`}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
                <div className="mt-3 p-3 bg-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-xl">
                  <p className="text-sm text-center">
                    <span className="text-[#D4AF37] font-bold">Total: ${getSelectedRoomPrice()} CAD</span>
                  </p>
                  <p className="text-xs text-neutral-400 text-center mt-1">
                    $30 deposit required to lock in • Pay rest at studio
                  </p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm text-neutral-400 mb-1">Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full bg-neutral-900/50 border border-[#D4AF37]/20 rounded-xl px-4 py-3 text-white"
                    required
                    data-testid="booking-name"
                  />
                </div>
                <div>
                  <label className="block text-sm text-neutral-400 mb-1">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full bg-neutral-900/50 border border-[#D4AF37]/20 rounded-xl px-4 py-3 text-white"
                    required
                    data-testid="booking-email"
                  />
                </div>
                <div>
                  <label className="block text-sm text-neutral-400 mb-1">Phone *</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full bg-neutral-900/50 border border-[#D4AF37]/20 rounded-xl px-4 py-3 text-white"
                    placeholder="(xxx) xxx-xxxx"
                    required
                    data-testid="booking-phone"
                  />
                </div>
                <div>
                  <label className="block text-sm text-neutral-400 mb-1">Notes (optional)</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full bg-neutral-900/50 border border-[#D4AF37]/20 rounded-xl px-4 py-3 text-white resize-none h-20"
                    placeholder="Project details, special requests..."
                    data-testid="booking-notes"
                  />
                </div>

                {/* Summary */}
                {selectedDate && selectedTime && (
                  <div className="bg-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-xl p-4 mt-4">
                    <p className="text-sm text-neutral-400 mb-2">Session Summary</p>
                    <p className="text-[#D4AF37] font-bold">{rooms.find(r => r.id === selectedRoom)?.name}</p>
                    <p className="text-white">{selectedDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} at {selectedTime}</p>
                    <p className="text-white">{durations.find(d => d.hours === selectedDuration)?.label}</p>
                    <p className="text-2xl font-bold gradient-text mt-2">${getSelectedRoomPrice()} CAD</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || !selectedDate || !selectedTime}
                  className={`btn-primary w-full flex items-center justify-center gap-2 ${(!selectedDate || !selectedTime) ? 'opacity-50 cursor-not-allowed' : ''}`}
                  data-testid="submit-booking-btn"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  ) : (
                    'Request Booking'
                  )}
                </button>
              </form>

              <p className="text-xs text-neutral-500 mt-4 text-center">
                Payment collected at the studio. Cancellation must be 24h in advance.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudioBookingPage;
